import toast from 'react-hot-toast';

import { ClickableText } from '@bitstopco/bitstop-theme';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  useGetWalletsConfigQuery,
  usePutAtmCoinsMutation,
} from '@/store/api/services/wallets/wallets';

import AssetBadge from '@/components/ui/AssetBadge';
import { CoinInfo } from '@/components/wallets';
import { sleep } from '@/helpers/sleep';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { useMemo, useState } from 'react';

const BulkToggleCoins = ({ hide, props }: ModalChildrenProps) => {
  const [putAtmCoins, { isLoading, isSuccess }] = usePutAtmCoinsMutation();
  const [selected, setSelected] = useState<string[]>([]);

  const toggleAsset = (id: string) => {
    const isSet = selected.includes(id);
    const newValue = isSet ? selected.filter((asset) => asset !== id) : [...selected, id];
    setSelected(newValue);
  };

  const { data } = useGetWalletsConfigQuery();
  const { coins, value, title, refetch } = useMemo(() => {
    const coins = data?.data?.[0].coins.filter(({ ticker }) => ticker !== 'BTC') ?? [];
    const value = !!props?.enable;
    return {
      coins,
      value,
      title: value ? 'Enable Assets' : 'Disable Assets',
      refetch: props?.refetch,
    };
  }, [data, props]);

  const handleSubmit = async () => {
    try {
      await putAtmCoins({
        atm_ids: props.selectedAtms.map(({ id }) => id),
        enable: value ? selected : [],
        disable: !value ? selected : [],
      }).unwrap();
      toast.success('Settings successfully saved');
      sleep(1000);
      refetch?.();
    } catch (err) {
      toast.error(`An unexpected error occurred during the action: ${err}`);
    }
  };

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 3 }}>
        <Stack gap={1}>
          <Typography variant="h6">Select assets to {value ? 'enable' : 'disable'}:</Typography>
          <Stack gap={2} py={1}>
            {coins.map((coin) => (
              <Stack
                key={coin.id}
                direction="row"
                justifyContent="space-between"
                sx={{ opacity: !coin.is_enabled ? 0.4 : 1 }}
              >
                <CoinInfo coin={coin} />
                <Checkbox onChange={() => toggleAsset(coin.id)} disabled={!coin.is_enabled} />
              </Stack>
            ))}
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box mt={2}>
          <Typography variant="h6" mb={2}>
            These ATMs will be impacted:
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ATM ID</TableCell>
                  <TableCell>ATM Name</TableCell>
                  <TableCell>Supported Assets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props?.selectedAtms?.map((atm: any) => (
                  <TableRow key={atm.id}>
                    <TableCell sx={{ width: '128px' }}>
                      <ClickableText
                        fixIcon
                        text={atm.id.toString()}
                        clipboardValue={atm.id.toString()}
                        name="Machine ID"
                        to={`${window.location.origin}/machine/${atm.id}`}
                      />
                    </TableCell>
                    <TableCell>{atm.name}</TableCell>
                    <TableCell>
                      <Stack py={1} direction="row" gap={1} flexWrap="wrap">
                        {atm.enabled_coins?.length === 0 && '-'}
                        {atm.enabled_coins?.map((coin) => (
                          <AssetBadge key={coin.id} coin={coin} />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!isSuccess && (
          <>
            <Button onClick={hide}>Cancel</Button>
            <LoadingButton
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading || selected.length === 0}
              loading={isLoading}
            >
              {title}
            </LoadingButton>
          </>
        )}
        {isSuccess && (
          <Button fullWidth onClick={hide}>
            Back to Machines
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default BulkToggleCoins;
